<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col class="align-center d-flex" cols="10">
					<v-toolbar-title>{{ $t('edit-observation-header') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>

		<v-card-text class="pt-4 pb-0 overflow-y-auto" style="height: calc(100vh - 221px)">
			<v-row dense class="ma-0">
				<v-col cols="6" class="mx-0">
					<v-select
						outlined
						v-model="performedBy"
						:label="$t('staff')"
						:items="medicalStaff"
						return-object
						item-text="fullName"
						:menu-props="{ maxHeight: 200, offsetY: true }"
						hide-details />
				</v-col>
				<v-col cols="6" class="mx-0">
					<v-select
						v-model="selectedObservationCategoryOption"
						:items="observationCategoryOptions"
						item-value="value"
						item-text="value"
						:label="$t('category')"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">
										{{ $t(item.value) }}
									</span>
								</template>
								{{ $t(item.description.toLowerCase().replace(/\s+/g, '-')) }}
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col cols="12" class="mx-0">
					<v-select
						v-model="selectedDiagnose"
						:items="diagnoses"
						item-value="diagnosisCode"
						:item-text="(item) => item.diagnosisCode"
						:label="$t('diagnose')"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">
										{{ $t(item.diagnosisCode) }}
									</span>
								</template>
								{{ $t(item.diagnoseDescription.toLowerCase().replace(/\s+/g, '-')) }}
							</v-tooltip>
						</template>
					</v-select>
				</v-col>

				<v-col cols="12" class="mx-0">
					<v-textarea
						outlined
						dense
						v-model="observationText"
						:label="$t('observation-text')"
						hide-details
						no-resize
						row-height="45"
						rows="15" />
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:inputLabel="$t('time-spent')"
						:dense="false" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip left v-if="!editButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('update') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="editObservation()" color="dark_purple" style="color: white" block>
						{{ $t('update') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapState } from 'vuex';
import { getRowsForDataTable } from '@/utils/utilities';
import { mapActions } from 'vuex/dist/vuex.common.js';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMObservationEdit',
	data () {
		return {
			loading: false,
			medicalStaff: null,
			additionalData: '',
			timeSpentSeconds: 0,
			selectedHospital: null,
			performedBy: '',
			selectedNoteTypeOption: '',
			observationCategoryOptions: [],
			selectedObservationCategoryOption: '',
			observationText: '',
			selectedDiagnose: '',
		};
	},
	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			ccmObservation: (state) => state.ccmObservation.ccmObservation,
			diagnoses: (state) => state.ccmDiagnoses.ccmDiagnosesByPatientId,
		}),
		headers () {
			return [
				{
					text: this.$t('prescription-brand-name'),
					value: 'brandName',
					class: 'table-header',
					align: 'start pl-2 pr-0',
				},
				{
					text: this.$t('dosage'),
					value: 'dosageInstructions',
					class: 'table-header',
					align: 'start pl-2 pr-0',
				},
				{
					text: this.$t('route'),
					value: 'route',
					class: 'table-header pl-2 pr-0',
					sortable: false,
				},
				{
					text: '',
					align: 'start pl-2 pr-0',
					sortable: false,
				},
			];
		},
		editButtonEnabled () {
			return this.observationText.trim() && this.timeSpentSeconds && this.timeSpentSeconds <= 7200;
		},
	},
	watch: {
		ccmObservation () {
			this.medicalStaff = this.filterMedicalStaff();

			this.performedBy = this.ccmObservation?.performedBy;
			this.additionalData = this.ccmObservation?.additionalData;
			this.selectedObservationCategoryOption = this.ccmObservation?.category;
			this.selectedDiagnose = this.ccmObservation?.relatedDiagnosisCode;
			this.observationText = this.ccmObservation?.observationText;
		},
		deep: true,
	},

	async mounted () {
		this.medicalStaff = this.filterMedicalStaff();
		const response = await this.getObservationCategoryOptionsTemplate();
		this.observationCategoryOptions = response.data;
		await this.getDiagnosisByPatientId(this.detailedUser.userData.id);
		this.performedBy = this.ccmObservation?.performedBy;
		this.additionalData = this.ccmObservation?.additionalData;
		this.selectedObservationCategoryOption = this.ccmObservation?.category;
		this.selectedDiagnose = this.ccmObservation?.relatedDiagnosisCode;
		this.observationText = this.ccmObservation?.observationText;
	},

	methods: {
		...mapActions({
			getObservationCategoryOptionsTemplate: 'ccmRecords/getObservationCategoryOptionsTemplate',
			getDiagnosisByPatientId: 'ccmDiagnoses/getCCMDiagnosesByPatientId',
		}),
		getRowsForDataTable,
		closeDrawer () {
			this.timeSpentSeconds = 0;

			this.$store.dispatch('prescriptions/clearSearchedPrescriptions');
			this.$store.commit('SET_DETAILS_DRAWER', false);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', false);
			this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
		},
		async editObservation () {
			const body = {
				hospitalId: this.currentRecord.hospitalId,
				observationDate: this.ccmObservation?.observationDate,
				category: this.selectedObservationCategoryOption,
				observationText: this.observationText,
				staffId: this.detailedUser.userData.id,
				performedBy: this.performedBy,
				performedByRole: '',
				timeSpentSeconds: this.timeSpentSeconds,
				additionalData: '',
				relatedDiagnosisCode: this.selectedDiagnose,
			};
			try {
				const payloadBody = {
					id: this.ccmObservation.id,
					body: body,
				};
				await this.$store.dispatch('ccmObservation/updateCCMObservation', payloadBody);
				await this.$store.dispatch('ccmObservation/getCCMObservationByPatientId', this.detailedUser.userData.id);
			} catch (error) {
				// Change this to something more suitable
				this.$store.commit('alerts/add', {
					id: 'editedObservation',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			} finally {
				this.closeDrawer();
			}
		},

		filterMedicalStaff () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList
				.filter((member) => (member.roleId === 3 || member.roleId === 4) && member.isSequoiaUser && member.status === 1)
				.map((member) => ({
					...member,
					fullName: `${member.firstName} ${member.lastName}`,
				}));
		},
	},
	components: { InputTimeFormatter },
};
</script>
